/**
 * Plausible lead tracking for outbound clicks to the Thomann shop.
 *
 * Fires the custom event `Thomann_Click` for every click on a link that leads
 * to the Thomann shop. A single delegated listener on the document also covers
 * links that are injected or revealed later by Alpine or Splide, so no
 * component needs its own tracking code.
 *
 * Links may enrich the event through data attributes, either on the link
 * itself or on any ancestor:
 *   data-track-position="top_seller"     which UI element the link belongs to
 *   data-product-name="Vanguard 14x6.5"  the product the link refers to
 *
 * @see https://plausible.io/docs/custom-event-goals
 * @see https://plausible.io/docs/custom-props/introduction
 */

const EVENT_NAME = 'Thomann_Click';

/**
 * Only shop hosts count as a lead. The block composers build product links on
 * the bare `thomann.de` host, the snare chooser uses `www.thomann.de`, and
 * editorial or ACF links may use any of the four. thomannmusic.com is the
 * international shop. Asset hosts such as images.static-thomann.de or
 * audio1.thomann.de are excluded on purpose.
 */
const SHOP_HOSTNAMES = [
    'www.thomann.de',
    'thomann.de',
    'www.thomannmusic.com',
    'thomannmusic.com',
];

/**
 * Fallback positions derived from the document structure, used when a link
 * carries no explicit data-track-position. Evaluated in order, so the more
 * specific landmarks have to come before <main>.
 *
 * <footer> is checked before <nav> because layouts/app.blade.php renders the
 * footer a second time inside the mobile menu, which lives inside the <nav>
 * landmark.
 */
const STRUCTURAL_POSITIONS = [
    { selector: 'header', position: 'header' },
    { selector: 'footer', position: 'footer' },
    { selector: 'nav', position: 'navigation' },
    { selector: 'main', position: 'content' },
];

const POSITION_FALLBACK = 'unknown';

/**
 * Parses a link href into a URL object.
 *
 * @param {HTMLAnchorElement} link
 * @returns {URL|null} null for hrefs that cannot be parsed (e.g. "javascript:")
 */
function parseHref(link) {
    const href = link.getAttribute('href');

    if (!href) {
        return null;
    }

    try {
        return new URL(href, window.location.href);
    } catch (error) {
        return null;
    }
}

/**
 * Checks whether a URL points at the Thomann shop.
 *
 * @param {URL} url
 * @returns {boolean}
 */
function isShopUrl(url) {
    return SHOP_HOSTNAMES.indexOf(url.hostname) !== -1;
}

/**
 * Strips query string and hash so tracking parameters and anchors do not
 * inflate the number of distinct property values in Plausible.
 *
 * @param {URL|Location} url
 * @returns {string}
 */
function normalizeUrl(url) {
    return url.origin + url.pathname;
}

/**
 * Resolves where on the page the link was placed.
 *
 * @param {HTMLAnchorElement} link
 * @returns {string}
 */
function resolvePosition(link) {
    const tagged = link.closest('[data-track-position]');

    if (tagged && tagged.dataset.trackPosition) {
        return tagged.dataset.trackPosition.trim();
    }

    for (let i = 0; i < STRUCTURAL_POSITIONS.length; i++) {
        if (link.closest(STRUCTURAL_POSITIONS[i].selector)) {
            return STRUCTURAL_POSITIONS[i].position;
        }
    }

    return POSITION_FALLBACK;
}

/**
 * Resolves the product the link refers to, if any.
 *
 * @param {HTMLAnchorElement} link
 * @returns {string} empty string when the link is not product related
 */
function resolveProductName(link) {
    const tagged = link.closest('[data-product-name]');

    if (!tagged || !tagged.dataset.productName) {
        return '';
    }

    return tagged.dataset.productName.trim();
}

/**
 * Sends the event. The Plausible script posts with `keepalive`, so the request
 * survives the navigation that the click triggers. No preventDefault needed.
 *
 * @param {HTMLAnchorElement} link
 * @param {URL} url
 */
function trackClick(link, url) {
    if (typeof window.plausible !== 'function') {
        return;
    }

    const props = {
        target_url: normalizeUrl(url),
        page_url: normalizeUrl(window.location),
        position: resolvePosition(link),
    };

    const productName = resolveProductName(link);

    if (productName) {
        props.product_name = productName;
    }

    window.plausible(EVENT_NAME, {props});
}

/**
 * Handles a click anywhere in the document and tracks it when it hit an
 * outbound shop link.
 *
 * @param {MouseEvent} event
 */
function onClick(event) {
    const link = event.target.closest ? event.target.closest('a[href]') : null;

    if (!link) {
        return;
    }

    const url = parseHref(link);

    if (!url || !isShopUrl(url)) {
        return;
    }

    trackClick(link, url);
}

/**
 * Registers the delegated listeners.
 */
export default function initPlausibleThomannClick() {
    // Capture phase, so the event is still tracked when a component stops
    // propagation in its own click handler.
    document.addEventListener('click', onClick, true);

    // Middle click opens the link in a new tab without firing `click`.
    document.addEventListener(
        'auxclick',
        (event) => {
            if (event.button === 1) {
                onClick(event);
            }
        },
        true
    );
}
